/**
 * Date/Time Formatting Utilities
 *
 * ใช้รูปแบบ 24 ชั่วโมงทั้งระบบ (24-hour format)
 *
 * Error Handling:
 * - ทุกฟังก์ชันมี try-catch เพื่อป้องกัน crash
 * - ตรวจสอบ null/undefined ก่อนประมวลผล
 * - Validate date ก่อนใช้งาน
 * - Return '-' เมื่อเกิด error
 */

/**
 * ✅ Helper: Validate และ convert เป็น Date object
 * @param date - Date object or ISO string
 * @returns Valid Date object or null
 */
function safeParseDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null;

  try {
    const d = date instanceof Date ? date : new Date(date);

    // ✅ ตรวจสอบว่าเป็น valid date
    if (isNaN(d.getTime())) {
      console.warn('[safeParseDate] Invalid date:', date);
      return null;
    }

    // ✅ ตรวจสอบว่าไม่ใช่ปี 0 หรือปีที่ผิดปกติ
    const year = d.getFullYear();
    if (year < 1900 || year > 2200) {
      console.warn('[safeParseDate] Year out of reasonable range:', year);
      return null;
    }

    return d;
  } catch (error) {
    console.error('[safeParseDate] Error parsing date:', error);
    return null;
  }
}

/**
 * Format date and time in 24-hour format (Thai style)
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11 ก.พ. 2026, 14:30")
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    // Thai locale with 24-hour format and Thai month names
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',  // ← แสดงเป็น "ก.พ." แทน "02"
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,  // ← บังคับ 24 ชม.
    });
  } catch (error) {
    console.error('[formatDateTime] Error:', error);
    return '-';
  }
}

/**
 * Format date only (no time) - Thai style
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11 ก.พ. 2026")
 */
export function formatDate(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    // Thai locale with Thai month names (short)
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',  // ← แสดงเป็น "ก.พ." แทน "02"
      day: 'numeric',
    });
  } catch (error) {
    console.error('[formatDate] Error:', error);
    return '-';
  }
}

/**
 * Format date with full month name - Thai style
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11 กุมภาพันธ์ 2026")
 */
export function formatDateLong(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    // Thai locale with full month name
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',  // ← แสดงเป็น "กุมภาพันธ์" (เต็ม)
      day: 'numeric',
    });
  } catch (error) {
    console.error('[formatDateLong] Error:', error);
    return '-';
  }
}

/**
 * Format time only (24-hour format)
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "14:30")
 */
export function formatTime(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    return d.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,  // ← บังคับ 24 ชม.
    });
  } catch (error) {
    console.error('[formatTime] Error:', error);
    return '-';
  }
}

/**
 * Format date and time with seconds (24-hour format) - Thai style
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11 ก.พ. 2026, 14:30:45")
 */
export function formatDateTimeWithSeconds(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',  // ← แสดงเป็น "ก.พ." แทน "02"
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,  // ← บังคับ 24 ชม.
    });
  } catch (error) {
    console.error('[formatDateTimeWithSeconds] Error:', error);
    return '-';
  }
}

/**
 * Format date in numeric format (for reports/exports)
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11/02/2026")
 */
export function formatDateNumeric(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    // Numeric format DD/MM/YYYY
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    console.error('[formatDateNumeric] Error:', error);
    return '-';
  }
}

/**
 * Format date and time in numeric format (for reports/exports)
 * @param date - Date object or ISO string
 * @returns Formatted string (e.g., "11/02/2026, 14:30")
 */
export function formatDateTimeNumeric(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    console.error('[formatDateTimeNumeric] Error:', error);
    return '-';
  }
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param date - Date object or ISO string
 * @returns Formatted string
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '-';

  try {
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'เมื่อสักครู่';
    if (diffMinutes < 60) return `${diffMinutes} นาทีที่แล้ว`;
    if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;
    if (diffDays < 7) return `${diffDays} วันที่แล้ว`;

    // If more than 7 days, show full date
    return formatDateTime(d);
  } catch (error) {
    console.error('[formatRelativeTime] Error:', error);
    return '-';
  }
}

/**
 * Convert Date to YYYY-MM-DD format (for date inputs)
 * @param date - Date object or ISO string
 * @returns YYYY-MM-DD string or empty string if invalid
 */
export function toDateInputValue(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '';

  try {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('[toDateInputValue] Error:', error);
    return '';
  }
}

/**
 * Convert Date to YYYY-MM-DDTHH:mm format (for datetime-local inputs)
 * @param date - Date object or ISO string
 * @returns YYYY-MM-DDTHH:mm string or empty string if invalid
 */
export function toDateTimeInputValue(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '';

  try {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('[toDateTimeInputValue] Error:', error);
    return '';
  }
}

/**
 * Convert Date to HH:mm format (for time inputs)
 * @param date - Date object or ISO string
 * @returns HH:mm string (24-hour) or empty string if invalid
 */
export function toTimeInputValue(date: Date | string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return '';

  try {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (error) {
    console.error('[toTimeInputValue] Error:', error);
    return '';
  }
}
